import { doc, updateDoc } from 'firebase/firestore';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../Firebase';
import { useHtmlStepperUpdater } from '../../contexts/HtmlStepperUpdaterContext';
import { UserContext } from '../../contexts/UserContext';
import EditStepperActionModal, {
  parseHtmlActionToStepperAction,
  stepperActionToHtmlAttributes,
} from '../../modals/EditStepperActionModal';
import ImageLibraryModal from '../../modals/ImageLibraryModal/ImageLibraryModal';
import { deleteQuiz, updateQuiz } from '../../services/QuizService';
import { DynamicPageSettingsModal } from '../ManageDynamicPage/DynamicPageSettingsModal';
import EditHTMLStepperFieldModal, {
  EditFieldModalData,
} from './EditHTMLStepperFieldModal';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';

/**
 * Renders all modals for the HtmlStepperEditor.
 * Handles image selection and action editing events from the preview iframe.
 */
export const HtmlStepperEditorModals = () => {
  const navigate = useNavigate();
  const { selectedAccount } = useContext(UserContext);
  const { modals, data, quizId, onClose } = useHtmlStepperEditor();
  const { dynamicPageId, dynamicPage, formId, form, formData, updateFormData } =
    data;
  const {
    selectImageModalData,
    setSelectImageModalData,
    selectThumbnailModalData,
    setSelectThumbnailModalData,
    editHtmlActionModalData,
    setEditHtmlActionModalData,
    editFieldModalData,
    setEditFieldModalData,
    pageSettingsModalOpen,
    setPageSettingsModalOpen,
  } = modals;

  const { createStepperUpdate, applyFormChange } = useHtmlStepperUpdater();

  // Compute all existing field IDs for uniqueness validation
  const allFieldIds = useMemo(() => {
    if (!formData?.steps) return [];
    const ids: string[] = [];
    formData.steps.forEach((step) => {
      step.fields?.forEach((field) => {
        ids.push(field.id);
      });
    });
    return ids;
  }, [formData?.steps]);

  // Handler for updating page data through Quiz service
  // This ensures both Quiz and DynamicPage stay in sync
  const handleUpdatePage = useCallback(
    async (pageData: any, _mergeFields?: string[]) => {
      if (!selectedAccount || !quizId) return;

      // Only call updateQuiz if there are relevant fields
      if (Object.keys(pageData).length > 0) {
        await updateQuiz(selectedAccount.id, quizId, pageData);
      }
    },
    [selectedAccount, quizId],
  );

  // Handler for updating path/metadata through Quiz service
  const handleUpdateMetadata = useCallback(
    async (metadata: { path?: string; alternatePathes?: string[] }) => {
      if (!selectedAccount || !quizId) return;

      await updateQuiz(selectedAccount.id, quizId, {
        path: metadata.path,
        alternatePathes: metadata.alternatePathes,
      });
    },
    [selectedAccount, quizId],
  );

  // Handler for deleting the quiz
  const handleDeleteQuiz = useCallback(async () => {
    if (!selectedAccount || !quizId) return;

    await deleteQuiz(selectedAccount.id, quizId);
    setPageSettingsModalOpen(false);
    onClose();
    navigate('/quiz-funnels');
  }, [selectedAccount, quizId, setPageSettingsModalOpen, onClose, navigate]);

  // Handler for saving quiz options (form name and auto-advance delay)
  const handleSaveQuizOptions = useCallback(
    async (options: { formName: string; autoAdvanceDelay: number }) => {
      if (!selectedAccount || !formId) return;

      // Update form name and quiz name directly on the form document
      const formRef = doc(db, 'accounts', selectedAccount.id, 'forms', formId);
      await updateDoc(formRef, {
        name: options.formName,
        updatedAt: new Date(),
      });

      // Also update the quiz name to keep them in sync
      if (quizId) {
        await updateQuiz(selectedAccount.id, quizId, {
          name: options.formName,
        });
      }

      // Update auto-advance delay in local form data (this will trigger autosave)
      updateFormData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          config: {
            ...prev.config,
            autoAdvanceDelay: options.autoAdvanceDelay,
          },
        };
      });
    },
    [selectedAccount, quizId, formId, updateFormData],
  );

  return (
    <>
      {/* Image Library Modal */}
      <ImageLibraryModal
        open={!!selectImageModalData}
        onClose={() => setSelectImageModalData(null)}
        onSelected={(images) => {
          if (
            !selectImageModalData ||
            !form?.development ||
            !images[0] ||
            !formId
          ) {
            setSelectImageModalData(null);
            return;
          }

          const { target, elementPath } = selectImageModalData;

          // Store current state for undo
          const before = JSON.parse(JSON.stringify(form.development));

          // Create update with the selected image
          const update = createStepperUpdate(
            formId,
            target,
            elementPath,
            images[0], // AssetData object
            'selectImage',
            before,
          );

          if (update) {
            applyFormChange(update);
          }

          setSelectImageModalData(null);
        }}
        types={['image', 'video']}
      />

      {/* Thumbnail Selection Modal (for video poster images) */}
      <ImageLibraryModal
        open={!!selectThumbnailModalData}
        onClose={() => setSelectThumbnailModalData(null)}
        onSelected={(images) => {
          if (
            !selectThumbnailModalData ||
            !form?.development ||
            !images[0] ||
            !formId
          ) {
            setSelectThumbnailModalData(null);
            return;
          }

          const { target, elementPath } = selectThumbnailModalData;

          // Store current state for undo
          const before = JSON.parse(JSON.stringify(form.development));

          // Create update with the selected thumbnail image
          const update = createStepperUpdate(
            formId,
            target,
            elementPath,
            images[0], // AssetData object
            'selectThumbnail',
            before,
          );

          if (update) {
            applyFormChange(update);
          }

          setSelectThumbnailModalData(null);
        }}
        types={['image']}
      />

      {/* Edit Stepper Action Modal */}
      <EditStepperActionModal
        open={!!editHtmlActionModalData}
        onClose={() => setEditHtmlActionModalData(null)}
        formData={formData}
        data={
          editHtmlActionModalData
            ? parseHtmlActionToStepperAction({
                action: editHtmlActionModalData.action ?? 'none',
                url: editHtmlActionModalData.url,
                openInNewTab: editHtmlActionModalData.openInNewTab,
              })
            : undefined
        }
        onSave={(actionData) => {
          if (!editHtmlActionModalData || !form?.development || !formId) {
            setEditHtmlActionModalData(null);
            return;
          }

          const { target, elementPath } = editHtmlActionModalData;

          // Store current state for undo
          const before = JSON.parse(JSON.stringify(form.development));

          // Convert action data back to HTML attributes
          const htmlAttrs = stepperActionToHtmlAttributes(actionData);

          // Create update with the action data
          const update = createStepperUpdate(
            formId,
            target,
            elementPath,
            {
              ...htmlAttrs,
              tagName: editHtmlActionModalData.tagName,
              text: editHtmlActionModalData.text,
            },
            'editHtmlAction',
            before,
          );

          if (update) {
            applyFormChange(update);
          }

          setEditHtmlActionModalData(null);
        }}
      />

      {/* Edit Field Modal */}
      <EditHTMLStepperFieldModal
        open={!!editFieldModalData}
        data={editFieldModalData?.field ?? null}
        existingFieldIds={allFieldIds}
        onClose={() => setEditFieldModalData(null)}
        onSave={(fieldData: EditFieldModalData) => {
          if (!form?.development || !editFieldModalData || !formId) {
            setEditFieldModalData(null);
            return;
          }

          const { stepId, field } = editFieldModalData;

          // Store current state for undo
          const before = JSON.parse(JSON.stringify(form.development));

          // Always derive elementPath from field.id
          const elementPath = `[data-sf-field="${field.id}"]`;
          const target = { type: 'step' as const, stepId };

          const update = createStepperUpdate(
            formId,
            target,
            elementPath,
            fieldData,
            'editField',
            before,
          );

          if (update) {
            applyFormChange(update);
          }

          setEditFieldModalData(null);
        }}
      />

      {/* Quiz Settings Modal (DynamicPage settings customized for Quiz) */}
      <DynamicPageSettingsModal
        open={pageSettingsModalOpen}
        setOpen={setPageSettingsModalOpen}
        dynamicPageId={dynamicPageId ?? undefined}
        dynamicPage={dynamicPage ?? undefined}
        defaultTab="general"
        visibleTabs={[
          'general',
          'quiz-options',
          'custom-code',
          'tracking',
          'integrations',
        ]}
        dialogTitle="Quiz Settings"
        hideGeneralFields={['goal', 'logoLink']}
        deleteConfig={{
          buttonText: 'Delete Quiz',
          // eslint-disable-next-line quotes
          confirmTitle: "Are you sure you'd like to delete this Quiz?",
          onDelete: handleDeleteQuiz,
        }}
        quizOptions={{
          formName: form?.name ?? '',
          autoAdvanceDelay: formData?.config?.autoAdvanceDelay ?? 300,
          onSave: handleSaveQuizOptions,
        }}
        onUpdatePage={handleUpdatePage}
        onUpdateMetadata={handleUpdateMetadata}
      />
    </>
  );
};
